import {
  autoPlacement,
  autoUpdate,
  computePosition,
  shift,
  size,
} from "@floating-ui/dom";

import { round } from "../utils";

/**
 * @typedef {import('@floating-ui/dom').ComputePositionReturn & { target: HTMLElement; relatedTarget: HTMLElement }} PositionChangedEntry
 */

/**
 * @callback PositionChangedCallback
 * @param {PositionChangedEntry} entry
 * @returns {void}
 */

/**
 * @type {PositionChangedCallback}
 */
const defaultCallback = ({
  middlewareData: { anchorPoint },
  strategy,
  x,
  y,
  target,
}) => {
  Object.assign(target.style, {
    left: 0,
    position: strategy,
    top: 0,
    transform: `translate(${round(x)}px, ${round(y)}px)`,
    transformOrigin: `${anchorPoint.x} ${anchorPoint.y}`,
  });
};

export default class AnchorPositionObserver {
  /**
   * @param {PositionChangedCallback} positionChangedCallback
   * @param {AnchorPositionObserverOptions} options
   */
  constructor(positionChangedCallback = defaultCallback) {
    this.positionChangedCallback = positionChangedCallback;
    this.disconnect = () => {};
  }

  /**
   *
   * @param {HTMLElement} anchorElement
   * @param {HTMLElement} positionedElement
   * @returns {void}
   */
  observe(anchorElement, positionedElement) {
    if (!anchorElement || !positionedElement) {
      return;
    }

    const cleanup = autoUpdate(anchorElement, positionedElement, async () => {
      if (anchorElement && positionedElement) {
        const offset = getContainerSize().offset;
        const bundle = await computePosition(anchorElement, positionedElement, {
          strategy: "fixed",
          placement: "bottom",
          middleware: [
            shift(() => ({ padding: { left: offset, right: offset } })),
            autoPlacement({
              allowedPlacements: [
                "bottom",
                "bottom-end",
                "bottom-start",
                "top",
                "top-end",
                "top-start",
              ],
            }),
            anchorPointMiddleware(),
            size({
              apply({ availableHeight, availableWidth, elements }) {
                Object.assign(elements.floating.style, {
                  maxBlockSize: `${availableHeight}px`,
                  maxInlineSize: `${availableWidth}px`,
                });
              },
              padding: { left: offset, right: offset },
            }),
          ],
        });
        this.positionChangedCallback({
          ...bundle,
          relatedTarget: anchorElement,
          target: positionedElement,
        });
      }
    });

    this.disconnect = () => {
      cleanup();
      this.disconnect = () => {};
    };
  }

  /**
   * @type {PositionChangedCallback}
   */
  positionChangedCallback;

  /**
   * @type {() => void}
   */
  disconnect;
}

function anchorPointMiddleware() {
  return {
    name: "anchorPoint",
    /** @param {import('@floating-ui/dom').MiddlewareState} args */
    fn(args) {
      const rect = args.rects.reference;
      const x = round(rect.x - args.x + rect.width / 2, 0);
      return { data: { x: `${x}px`, y: "top" } };
    },
  };
}

/**
 * Returns sizing derived from the global .container class.
 */
function getContainerSize() {
  if (typeof document === "undefined") {
    return { width: 0, offset: 0 };
  }
  let container = document.getElementById("ifrs-container-size-ref");
  if (!container) {
    container = document.createElement("div");
    container.id = "ifrs-container-size-ref";
    container.setAttribute("aria-hidden", "true");
    container.style.height = "0";
    container.classList.add("container");
  }
  if (!container.offsetParent) {
    document.body.append(container);
  }
  return {
    width: container.clientWidth,
    offset: container.getBoundingClientRect().left,
  };
}
